import React from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNow } from 'date-fns';

const RecentActivities = ({ activities = [], loading = false, maxItems = 5 }) => {
  if (loading) {
    return (
      <div className="recent-activities loading">
        <div className="activity-item">Loading activities...</div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="recent-activities empty">
        <div className="activity-item">No recent activities found</div>
      </div>
    );
  }

  const formatAmount = (amount, currency = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency === 'USDC' ? 'USD' : currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `${amount >= 0 ? '+' : ''}${amount.toFixed(2)} ${currency}`;
    }
  };

  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'deposit':
        return '↑';
      case 'withdrawal':
        return '↓';
      case 'transfer':
        return '↔';
      case 'purchase':
        return '↓'; // Stock purchase (cash out)
      case 'sale':
        return '↑'; // Stock sale (cash in)
      default:
        return '•';
    }
  };

  return (
    <div className="recent-activities">
      <h3 className="recent-activities-title">Recent Activities</h3>
      <div className="activities-list">
        {activities.slice(0, maxItems).map((activity, index) => (
          <div key={activity.id || index} className="activity-item">
            <div className="activity-icon">
              {getActivityIcon(activity.type)}
            </div>
            <div className="activity-details">
              <div className="activity-description">
                {activity.description || `${activity.type} ${activity.currency || 'USDC'}`}
              </div>
              <div className="activity-meta">
                <span className="activity-time">
                  {activity.timestamp ? formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true }) : 'Just now'}
                </span>
              </div>
            </div>
            {typeof activity.amount === 'number' && (
              <div className={`activity-amount ${activity.amount >= 0 ? 'positive' : 'negative'}`}>
                {activity.amount >= 0 ? '+' : ''}{formatAmount(activity.amount, activity.currency)}
              </div>
            )}
          </div>
        ))}
      </div>
      <style jsx>{`
        .recent-activities {
          width: 100%;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .recent-activities-title {
          font-size: 1rem;
          font-weight: 600;
          color: #333;
          margin: 0 0 1rem 0;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #eee;
        }
        .activities-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .activity-item {
          display: flex;
          align-items: center;
          padding: 0.75rem;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .activity-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        .activity-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #f0f4ff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          font-weight: bold;
          color: #2d6ae3;
        }
        .activity-details {
          flex: 1;
        }
        .activity-description {
          font-weight: 500;
          color: #333;
          margin-bottom: 2px;
        }
        .activity-meta {
          display: flex;
          align-items: center;
          font-size: 0.8rem;
          color: #888;
        }
        .activity-time {
          font-size: 0.75rem;
        }
        .activity-amount {
          font-weight: 600;
          font-size: 0.9rem;
        }
        .activity-amount.positive {
          color: #16c784;
        }
        .activity-amount.negative {
          color: #ea3943;
        }
        .loading .activity-item,
        .empty .activity-item {
          justify-content: center;
          color: #888;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

RecentActivities.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      amount: PropTypes.number,
      currency: PropTypes.string,
      description: PropTypes.string,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    })
  ),
  loading: PropTypes.bool,
  maxItems: PropTypes.number
};

export default RecentActivities;
